/**
 * @copyright Copyright 2016 The MathWorks, Inc.
 * @description Resizable provides API to make a widget resizable
 * @module mw-dialogs/controller/Resizable
 */
define([
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/on",
    'dojo/query',
    "dojo/window",
    "./stopEvent"
], function (domClass, domConstruct, domGeometry, domStyle, on, query, win, stopEvent) {

    let getWidgetWidthAfterResizeEast =
        (widget, widgetDimension, event, resizeOffset, windowDimension, bindingNodeDimension) => {
            let widgetWidthAfterResize;

            if (bindingNodeDimension) {
                widgetWidthAfterResize =
                    Math.max(domStyle.get(widget.domNode, "min-width"),
                        Math.min(windowDimension.w - widgetDimension.x,
                            Math.min(event.pageX - resizeOffset.x
                                - widgetDimension.x,
                                Math.min(domStyle.get(widget.domNode, "max-width") ?
                                        domStyle.get(widget.domNode, "max-width") :
                                    bindingNodeDimension.w + bindingNodeDimension.x -
                                    widgetDimension.x,
                                    bindingNodeDimension.w + bindingNodeDimension.x -
                                    widgetDimension.x))));
            } else {
                widgetWidthAfterResize =
                    Math.max(domStyle.get(widget.domNode, "min-width"),
                        Math.min(event.pageX - resizeOffset.x - widgetDimension.x,
                            Math.min(domStyle.get(widget.domNode, "max-width") ?
                                    domStyle.get(widget.domNode, "max-width") :
                                windowDimension.w - widgetDimension.x,
                                windowDimension.w - widgetDimension.x)));
            }

            return widgetWidthAfterResize;
        };

    let getWidgetWidthAfterResizeWest = (widget, widgetDimension, event, resizeOffset,
                                         windowDimension, bindingNodeDimension) => {
        let widgetWidthAfterResize;

        if (bindingNodeDimension) {
            widgetWidthAfterResize =
                Math.max(domStyle.get(widget.domNode, "min-width"),
                    Math.min(widgetDimension.x + widgetDimension.w,
                        Math.min(widgetDimension.x + widgetDimension.w -
                            (event.pageX - resizeOffset.x),
                            Math.min(domStyle.get(widget.domNode, "max-width") ?
                                    domStyle.get(widget.domNode, "max-width") :
                                widgetDimension.x + widgetDimension.w -
                                bindingNodeDimension.x,
                                widgetDimension.x + widgetDimension.w -
                                bindingNodeDimension.x))));
        } else {
            widgetWidthAfterResize =
                Math.max(domStyle.get(widget.domNode, "min-width"),
                    Math.min(widgetDimension.x + widgetDimension.w -
                        (event.pageX - resizeOffset.x),
                        Math.min(domStyle.get(widget.domNode, "max-width") ?
                                domStyle.get(widget.domNode, "max-width") :
                            widgetDimension.x + widgetDimension.w,
                            widgetDimension.x + widgetDimension.w)));
        }

        return widgetWidthAfterResize;
    };

    let getWidgetHeightAfterResizeSouth =
        (widget, widgetDimension, event, resizeOffset, windowDimension, bindingNodeDimension) => {
            let widgetHeightAfterResize;

            if (bindingNodeDimension) {
                widgetHeightAfterResize =
                    Math.max(domStyle.get(widget.domNode, "min-height"),
                        Math.min(windowDimension.h - widgetDimension.y,
                            Math.min(event.pageY - resizeOffset.y
                                - widgetDimension.y,
                                Math.min(domStyle.get(widget.domNode, "max-height")
                                        ? domStyle.get(widget.domNode, "max-height")
                                        : bindingNodeDimension.h +
                                    bindingNodeDimension.y - widgetDimension.y,
                                    bindingNodeDimension.h + bindingNodeDimension.y -
                                    widgetDimension.y))));
            } else {
                widgetHeightAfterResize =
                    Math.max(domStyle.get(widget.domNode, "min-height"),
                        Math.min(event.pageY - resizeOffset.y - widgetDimension.y,
                            Math.min(domStyle.get(widget.domNode, "max-height") ?
                                    domStyle.get(widget.domNode, "max-height") :
                                windowDimension.h - widgetDimension.y,
                                windowDimension.h - widgetDimension.y)));
            }

            return widgetHeightAfterResize;
        };

    let getWidgetHeightAfterResizeNorth =
        (widget, widgetDimension, event, resizeOffset, windowDimension, bindingNodeDimension) => {
            let widgetHeightAfterResize;

            if (bindingNodeDimension) {
                widgetHeightAfterResize =
                    Math.max(domStyle.get(widget.domNode, "min-height"),
                        Math.min(widgetDimension.y + widgetDimension.h,
                            Math.min(widgetDimension.y + widgetDimension.h -
                                (event.pageY - resizeOffset.y),
                                Math.min(domStyle.get(widget.domNode, "max-height")
                                        ? domStyle.get(widget.domNode, "max-height")
                                        : widgetDimension.y + widgetDimension.h -
                                    bindingNodeDimension.y,
                                    widgetDimension.y + widgetDimension.h -
                                    bindingNodeDimension.y))));
            } else {
                widgetHeightAfterResize =
                    Math.max(domStyle.get(widget.domNode, "min-height"),
                        Math.min(widgetDimension.y + widgetDimension.h -
                            (event.pageY - resizeOffset.y),
                            Math.min(domStyle.get(widget.domNode, "max-height") ?
                                    domStyle.get(widget.domNode, "max-height") :
                                widgetDimension.y + widgetDimension.h,
                                widgetDimension.y + widgetDimension.h)));
            }

            return widgetHeightAfterResize;
        };

    let getWidgetDimensionAfterResize = (widget, widgetDimension, event, resizeOffset,
                                         windowDimension, bindingNode, horizontal, vertical) => {
        let widgetDimensionAfterResize = {};
        if (typeof bindingNode === 'object') {
            // Dimension of bindingNode which specifies the boundary to constrain the size
            // of the widget.
            let bindingNodeDimension = domGeometry.position(bindingNode);
            if (horizontal === "e") {
                widgetDimensionAfterResize.w =
                    getWidgetWidthAfterResizeEast(widget, widgetDimension, event, resizeOffset,
                        windowDimension, bindingNodeDimension);
            } else if (horizontal === "w") {
                widgetDimensionAfterResize.w =
                    getWidgetWidthAfterResizeWest(widget, widgetDimension, event, resizeOffset,
                        windowDimension, bindingNodeDimension);
            }

            if (vertical === "s") {
                widgetDimensionAfterResize.h =
                    getWidgetHeightAfterResizeSouth(widget, widgetDimension, event,
                        resizeOffset, windowDimension, bindingNodeDimension);
            } else if (vertical === "n") {
                widgetDimensionAfterResize.h =
                    getWidgetHeightAfterResizeNorth(widget, widgetDimension, event,
                        resizeOffset, windowDimension, bindingNodeDimension);
            }
        } else {
            if (horizontal === "e") {
                widgetDimensionAfterResize.w =
                    getWidgetWidthAfterResizeEast(widget, widgetDimension, event, resizeOffset,
                        windowDimension);
            } else if (horizontal === "w") {
                widgetDimensionAfterResize.w =
                    getWidgetWidthAfterResizeWest(widget, widgetDimension, event, resizeOffset,
                        windowDimension);
            }

            if (vertical === "s") {
                widgetDimensionAfterResize.h =
                    getWidgetHeightAfterResizeSouth(widget, widgetDimension, event,
                        resizeOffset, windowDimension);
            } else if (vertical === "n") {
                widgetDimensionAfterResize.h =
                    getWidgetHeightAfterResizeNorth(widget, widgetDimension, event,
                        resizeOffset, windowDimension);
            }
        }

        if (horizontal === "w") {
            widgetDimensionAfterResize.l = widgetDimension.x
                + widgetDimension.w - widgetDimensionAfterResize.w;
        }

        if (vertical === "n") {
            widgetDimensionAfterResize.t = widgetDimension.y
                + widgetDimension.h - widgetDimensionAfterResize.h;
        }

        return widgetDimensionAfterResize;
    };

    let setWidgetDimensionAfterResize = (widget, widgetDimension, event, resizeOffset,
                                         windowDimension, bindingNode, horizontal, vertical) => {
        stopEvent(event);

        let widgetDimensionAfterResize = getWidgetDimensionAfterResize(widget, widgetDimension,
            event, resizeOffset, windowDimension, bindingNode, horizontal, vertical);
        let contentSize = {};
        let contentPosition;

        if (horizontal === "e" || horizontal === "w") {
            widget.domNode.style.width = `${widgetDimensionAfterResize.w}px`;
        }

        if (horizontal === "w") {
            widget.domNode.style.left = `${widgetDimensionAfterResize.l}px`;
        }

        if (vertical === "s" || vertical === "n") {
            widget.domNode.style.height = `${widgetDimensionAfterResize.h}px`;
        }

        if (vertical === "n") {
            widget.domNode.style.top = `${widgetDimensionAfterResize.t}px`;
        }

        widgetDimension = domGeometry.position(widget.domNode);
        if (widget._contentArea) {
            contentPosition = domGeometry.position(widget._contentArea);
            contentSize = {w: contentPosition.w, h: contentPosition.h};
        }

        emitResizeEvent(widget, widgetDimension, contentSize);
    };

    /**
     * Emit resize event.
     *
     * @fires resize
     * @property {object} widget - The widget instance
     * @property {object} widgetDimension - The widget dimension
     * @property {object} contentSize - The widget's content area dimension
     */
    function emitResizeEvent(widget, widgetDimension, contentSize) {
        on.emit(widget.domNode, "resize", {
            bubbles: false,
            dimension: widgetDimension,
            contentSize: contentSize
        });
    }

    /**
     * @alias mw-dialogs/controller/Resizable
     */
    return {

        resizeEvent: "resize",

        /**
         * @function enable
         * @description This method makes a widget resizable
         *
         * @static
         *
         * @param {Object} [args] - argument object
         * @param {Object} [args.widget] - The widget instance
         * @param {Object} [args.bindingNode] - DOM Node which constraints the size of widget
         */
        enable (args) {

            let {widget, bindingNode} = args;

            widget._resizeEventSignals = [];

            let windowDimension,
                widgetDimension,
                resizeOffset = {
                    x: 0,
                    y: 0
                };

            let addResizeHandler = () => {
                let isResizeHandlerEastReady = false,
                    isResizeHandlerSouthReady = false,
                    isResizeHandlerWestReady = false,
                    isResizeHandlerNorthReady = false,
                    isResizeHandlerSouthEastReady = false,
                    isResizeHandlerNorthEastReady = false,
                    isResizeHandlerSouthWestReady = false,
                    isResizeHandlerNorthWestReady = false;

                widget._resizeHandlers = [];

                let resizeHandlerEast = document.createElement("div");
                domClass.add(resizeHandlerEast, "mwResizableHandle mwResizable-e");
                domConstruct.place(resizeHandlerEast, widget.domNode, "last");
                widget._resizeHandlers.push(resizeHandlerEast);

                let resizeHandlerSouth = document.createElement("div");
                domClass.add(resizeHandlerSouth, "mwResizableHandle mwResizable-s");
                domConstruct.place(resizeHandlerSouth, widget.domNode, "last");
                widget._resizeHandlers.push(resizeHandlerSouth);

                let resizeHandlerWest = document.createElement("div");
                domClass.add(resizeHandlerWest, "mwResizableHandle mwResizable-w");
                domConstruct.place(resizeHandlerWest, widget.domNode, "last");
                widget._resizeHandlers.push(resizeHandlerWest);

                let resizeHandlerNorth = document.createElement("div");
                domClass.add(resizeHandlerNorth, "mwResizableHandle mwResizable-n");
                domConstruct.place(resizeHandlerNorth, widget.domNode, "last");
                widget._resizeHandlers.push(resizeHandlerNorth);

                let resizeHandlerSouthEast = document.createElement("div");
                domClass.add(resizeHandlerSouthEast, "mwResizableHandle mwResizable-se");
                domConstruct.place(resizeHandlerSouthEast, widget.domNode, "last");
                widget._resizeHandlers.push(resizeHandlerSouthEast);

                let resizeHandlerNorthEast = document.createElement("div");
                domClass.add(resizeHandlerNorthEast, "mwResizableHandle mwResizable-ne");
                domConstruct.place(resizeHandlerNorthEast, widget.domNode, "last");
                widget._resizeHandlers.push(resizeHandlerNorthEast);

                let resizeHandlerSouthWest = document.createElement("div");
                domClass.add(resizeHandlerSouthWest, "mwResizableHandle mwResizable-sw");
                domConstruct.place(resizeHandlerSouthWest, widget.domNode, "last");
                widget._resizeHandlers.push(resizeHandlerSouthWest);

                let resizeHandlerNorthWest = document.createElement("div");
                domClass.add(resizeHandlerNorthWest, "mwResizableHandle mwResizable-nw");
                domConstruct.place(resizeHandlerNorthWest, widget.domNode, "last");
                widget._resizeHandlers.push(resizeHandlerNorthWest);

                widget._resizeHandlers.forEach((resizeHandler) => {
                    widget._resizeEventSignals.push(on(resizeHandler, 'mousedown', (e) => {

                        // Only resize on left click
                        if (e.button !== 0) {
                            return;
                        }

                        stopEvent(e);

                        windowDimension = win.getBox();
                        widgetDimension = domGeometry.position(widget.domNode);

                        if (resizeHandler === resizeHandlerEast) {
                            isResizeHandlerEastReady = true;
                            resizeOffset.x = e.pageX - (widgetDimension.x + widgetDimension.w);
                            // Fix cursor style when resizing
                            query('*')[0].style.cursor = 'ew-resize';
                        } else if (resizeHandler === resizeHandlerSouth) {
                            isResizeHandlerSouthReady = true;
                            resizeOffset.y = e.pageY - (widgetDimension.y + widgetDimension.h);
                            query('*')[0].style.cursor = 'ns-resize';
                        } else if (resizeHandler === resizeHandlerWest) {
                            isResizeHandlerWestReady = true;
                            resizeOffset.x = e.pageX - widgetDimension.x;
                            query('*')[0].style.cursor = 'ew-resize';
                        } else if (resizeHandler === resizeHandlerNorth) {
                            isResizeHandlerNorthReady = true;
                            resizeOffset.y = e.pageY - widgetDimension.y;
                            query('*')[0].style.cursor = 'ns-resize';
                        } else if (resizeHandler === resizeHandlerSouthEast) {
                            isResizeHandlerSouthEastReady = true;
                            resizeOffset.x = e.pageX - (widgetDimension.x + widgetDimension.w);
                            resizeOffset.y = e.pageY - (widgetDimension.y + widgetDimension.h);
                            query('*')[0].style.cursor = 'nwse-resize';
                        } else if (resizeHandler === resizeHandlerNorthEast) {
                            isResizeHandlerNorthEastReady = true;
                            resizeOffset.x = e.pageX - (widgetDimension.x + widgetDimension.w);
                            resizeOffset.y = e.pageY - widgetDimension.y;
                            query('*')[0].style.cursor = 'nesw-resize';
                        } else if (resizeHandler === resizeHandlerSouthWest) {
                            isResizeHandlerSouthWestReady = true;
                            resizeOffset.x = e.pageX - widgetDimension.x;
                            resizeOffset.y = e.pageY - (widgetDimension.y + widgetDimension.h);
                            query('*')[0].style.cursor = 'nesw-resize';
                        } else if (resizeHandler === resizeHandlerNorthWest) {
                            isResizeHandlerNorthWestReady = true;
                            resizeOffset.x = e.pageX - widgetDimension.x;
                            resizeOffset.y = e.pageY - widgetDimension.y;
                            query('*')[0].style.cursor = 'nwse-resize';
                        }
                    }));
                });

                widget._resizeEventSignals.push(on(document, 'mouseup', () => {
                    isResizeHandlerEastReady = false;
                    isResizeHandlerSouthReady = false;
                    isResizeHandlerWestReady = false;
                    isResizeHandlerNorthReady = false;
                    isResizeHandlerSouthEastReady = false;
                    isResizeHandlerNorthEastReady = false;
                    isResizeHandlerSouthWestReady = false;
                    isResizeHandlerNorthWestReady = false;
                    query('*')[0].style.cursor = '';
                }));


                widget._resizeEventSignals.push(on(document, 'mousemove', (e) => {
                    if (isResizeHandlerEastReady) {
                        setWidgetDimensionAfterResize(widget, widgetDimension, e, resizeOffset,
                            windowDimension, bindingNode, 'e');
                    } else if (isResizeHandlerSouthReady) {
                        setWidgetDimensionAfterResize(widget, widgetDimension, e, resizeOffset,
                            windowDimension, bindingNode, null, 's');
                    } else if (isResizeHandlerWestReady) {
                        setWidgetDimensionAfterResize(widget, widgetDimension, e, resizeOffset,
                            windowDimension, bindingNode, 'w');
                    } else if (isResizeHandlerNorthReady) {
                        setWidgetDimensionAfterResize(widget, widgetDimension, e, resizeOffset,
                            windowDimension, bindingNode, null, 'n');
                    } else if (isResizeHandlerSouthEastReady) {
                        setWidgetDimensionAfterResize(widget, widgetDimension, e, resizeOffset,
                            windowDimension, bindingNode, 'e', 's');
                    } else if (isResizeHandlerNorthEastReady) {
                        setWidgetDimensionAfterResize(widget, widgetDimension, e, resizeOffset,
                            windowDimension, bindingNode, 'e', 'n');
                    } else if (isResizeHandlerSouthWestReady) {
                        setWidgetDimensionAfterResize(widget, widgetDimension, e, resizeOffset,
                            windowDimension, bindingNode, 'w', 's');
                    } else if (isResizeHandlerNorthWestReady) {
                        setWidgetDimensionAfterResize(widget, widgetDimension, e, resizeOffset,
                            windowDimension, bindingNode, 'w', 'n');
                    }
                }));
            };

            addResizeHandler();

        },

        /**
         * @function disable
         * @description Disable the resizable capability of a widget
         *
         * @static
         *
         * @param {Object} [widget] - The widget instance
         */
        disable (widget) {
            widget._resizeEventSignals.forEach((eventSignal) => {
                eventSignal.remove();
            });

            widget._resizeEventSignals = [];

            widget._resizeHandlers.forEach((resizeHandler) => {
                domConstruct.destroy(resizeHandler);
            });

            widget._resizeHandlers = [];
        }
    };
});